// Personal reminders (reminders.js on the server): added from any day of the Dashboard calendar,
// with a time, a note, an optional alert, an optional email and an optional pin. Pinned
// reminders stay in a bar at the top of the journal until they're marked done.
import { useEffect, useState, useSyncExternalStore } from 'react';
import { api } from './api';
import { confirmDialog } from './confirmDialog';
import { ding, pushToast } from './MarketCalendar';
import { currentLocale } from './langState';
import { useI18n } from './i18n';
import { PushHint } from './PushHint';

const ALERTS = [['', 'No alert'], ['0', 'At the time'], ['5', '5 min before'], ['10', '10 min before'], ['15', '15 min before'], ['30', '30 min before']];
const FIRED_KEY = 'mm-reminders-fired';

// Every component showing reminders refetches when one is added, changed or deleted.
let version = 0;
const listeners = new Set();
const changed = () => { version += 1; listeners.forEach((fn) => fn()); };
const useVersion = () => useSyncExternalStore((fn) => { listeners.add(fn); return () => listeners.delete(fn); }, () => version);

// Reminders between two days (YYYY-MM-DD), plus every pinned one not done yet.
export function useReminders(from, to) {
  const v = useVersion();
  const [list, setList] = useState(null);
  useEffect(() => {
    let alive = true;
    api(`/reminders${from ? `?from=${from}&to=${to}` : ''}`).then((r) => alive && setList(r)).catch(() => alive && setList((old) => old || []));
    return () => { alive = false; };
  }, [from, to, v]);
  return list;
}

const timeLabel = (hhmm) => new Date(2026, 0, 1, ...hhmm.split(':').map(Number)).toLocaleTimeString(currentLocale(), { hour: 'numeric', minute: '2-digit' });
const dayLabel = (key) => new Date(`${key}T12:00:00`).toLocaleDateString(currentLocale(), { weekday: 'short', month: 'short', day: 'numeric' });
const zone = () => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return 'America/New_York'; } };

function ReminderForm({ date, reminder, onDone }) {
  const { t } = useI18n();
  const [form, setForm] = useState(() => ({
    time: reminder ? reminder.time : '08:00',
    note: reminder ? reminder.note : '',
    alert: reminder ? (reminder.alert === null ? '' : String(reminder.alert)) : '0',
    email: reminder ? reminder.email : false,
    pinned: reminder ? reminder.pinned : false,
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  async function save(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      // A click is when browsers allow sound and notifications to be readied.
      if (form.alert !== '') {
        ding();
        try { if ('Notification' in window && window.Notification.permission === 'default') window.Notification.requestPermission(); } catch { /* none */ }
      }
      const body = { date, time: form.time, zone: zone(), note: form.note, alert: form.alert === '' ? null : Number(form.alert), email: form.email, pinned: form.pinned };
      await api(reminder ? `/reminders/${reminder.id}` : '/reminders', { method: reminder ? 'PUT' : 'POST', body });
      changed();
      onDone();
    } catch (err) {
      setError(err.status === 403 ? t('Create your free account to save reminders.') : err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="reminder-form" onSubmit={save}>
      <div className="reminder-form-row">
        <label>{t('Time')}<input type="time" value={form.time} onChange={set('time')} required /></label>
        <label>{t('Alert')}
          <select value={form.alert} onChange={set('alert')}>
            {ALERTS.map(([v, label]) => <option key={v} value={v}>{t(label)}</option>)}
          </select>
        </label>
      </div>
      <label>{t('Note')}<textarea value={form.note} onChange={set('note')} rows={2} maxLength={500} required placeholder={t('e.g. CPI at 8:30, no trades before 8:45')} /></label>
      <div className="reminder-form-checks">
        <label className="reminder-check"><input type="checkbox" checked={form.email} onChange={set('email')} />{t('Email me')}</label>
        <label className="reminder-check"><input type="checkbox" checked={form.pinned} onChange={set('pinned')} />{t('Pin at the top of the app until done')}</label>
      </div>
      {form.alert !== '' && <PushHint />}
      {error && <p className="error">{error}</p>}
      <div className="reminder-form-actions">
        <button type="button" className="secondary small" onClick={onDone}>{t('Cancel')}</button>
        <button type="submit" className="small" disabled={busy}>{busy ? t('Saving…') : reminder ? t('Save reminder') : t('Add reminder')}</button>
      </div>
    </form>
  );
}

function badges(r, t) {
  return [
    r.alert !== null && (r.alert === 0 ? t('Alert at the time') : t('Alert {n} min before', { n: r.alert })),
    r.email && t('Email'),
    r.pinned && !r.done && t('Pinned'),
  ].filter(Boolean);
}

async function setDone(r, done) {
  await api(`/reminders/${r.id}`, { method: 'PUT', body: { done } });
  changed();
}

// Inside the Dashboard calendar's day pop-up: that day's reminders and the form to add one.
export function DayReminders({ date, reminders }) {
  const { t } = useI18n();
  const [editing, setEditing] = useState(null); // 'new' or a reminder id
  const list = (reminders || []).filter((r) => r.date === date).sort((a, b) => a.time.localeCompare(b.time));
  async function remove(r) {
    if (!(await confirmDialog({ title: t('Delete this reminder?'), message: r.note, confirmLabel: t('Delete'), cancelLabel: t('Cancel'), danger: true }))) return;
    await api(`/reminders/${r.id}`, { method: 'DELETE' });
    changed();
  }
  return (
    <section className="day-reminders" aria-label={t('Reminders')}>
      <div className="day-reminders-head">
        <span className="modal-section-label">{t('Reminders')}</span>
        {editing !== 'new' && <button type="button" className="secondary small" onClick={() => setEditing('new')}>+ {t('Add a reminder')}</button>}
      </div>
      {editing === 'new' && <ReminderForm date={date} onDone={() => setEditing(null)} />}
      {list.length > 0 && (
        <ul className="reminder-list">
          {list.map((r) => (editing === r.id ? (
            <li key={r.id}><ReminderForm date={date} reminder={r} onDone={() => setEditing(null)} /></li>
          ) : (
            <li key={r.id} className={`reminder-item${r.done ? ' done' : ''}`}>
              <span className="reminder-time">{timeLabel(r.time)}</span>
              <span className="reminder-body">
                <span className="reminder-note">{r.note}</span>
                {badges(r, t).length > 0 && <span className="reminder-badges">{badges(r, t).map((b) => <span key={b}>{b}</span>)}</span>}
              </span>
              <span className="reminder-actions">
                <button type="button" className="link-button" onClick={() => setDone(r, !r.done)}>{r.done ? t('Undo') : t('Done')}</button>
                <button type="button" className="link-button" onClick={() => setEditing(r.id)}>{t('Edit')}</button>
                <button type="button" className="link-button danger-link" onClick={() => remove(r)}>{t('Delete')}</button>
              </span>
            </li>
          )))}
        </ul>
      )}
      {!list.length && editing !== 'new' && <p className="reminder-empty">{t('No reminders on this day.')}</p>}
    </section>
  );
}

// The bar at the top of the journal with every pinned reminder that isn't done.
export function PinnedReminders() {
  const { t } = useI18n();
  const list = useReminders(null, null);
  const pinned = (list || []).filter((r) => r.pinned && !r.done).sort((a, b) => new Date(a.at) - new Date(b.at));
  if (!pinned.length) return null;
  return (
    <ul className="pinned-reminders" aria-label={t('Pinned reminders')}>
      {pinned.map((r) => (
        <li key={r.id} className={new Date(r.at) < new Date() ? 'past' : ''}>
          <span className="pinned-icon" aria-hidden="true">📌</span>
          <span className="pinned-when">{dayLabel(r.date)} · {timeLabel(r.time)}</span>
          <span className="pinned-note">{r.note}</span>
          <button type="button" className="secondary small" onClick={() => setDone(r, true)}>{t('Done')}</button>
        </li>
      ))}
    </ul>
  );
}

function readFired() {
  try { return JSON.parse(localStorage.getItem(FIRED_KEY)) || {}; } catch { return {}; }
}

// Rings each reminder's alert once while the journal is open: a sound, a message that stays until
// closed, and a system notification when the browser allows it.
export function ReminderAlertRunner() {
  const { t } = useI18n();
  const today = new Date();
  const from = new Date(today.getTime() - 86400000).toLocaleDateString('en-CA');
  const to = new Date(today.getTime() + 2 * 86400000).toLocaleDateString('en-CA');
  const list = useReminders(from, to);
  useEffect(() => {
    if (!list) return undefined;
    const check = () => {
      const now = Date.now();
      const fired = readFired();
      const due = list.filter((r) => r.alert !== null && r.notifyAt && !r.done && !fired[r.id]
        && now >= new Date(r.notifyAt).getTime() && now < new Date(r.at).getTime() + 30 * 60000);
      if (!due.length) return;
      due.forEach((r) => { fired[r.id] = now; });
      Object.keys(fired).forEach((id) => { if (now - fired[id] > 7 * 86400000) delete fired[id]; });
      try { localStorage.setItem(FIRED_KEY, JSON.stringify(fired)); } catch { /* rings again next visit */ }
      ding();
      due.forEach((r) => {
        const mins = Math.round((new Date(r.at) - now) / 60000);
        const title = mins > 0 ? t('Reminder in {n} min · {time}', { n: mins, time: timeLabel(r.time) }) : t('Reminder · {time}', { time: timeLabel(r.time) });
        try {
          if ('Notification' in window && window.Notification.permission === 'granted') new window.Notification(title, { body: r.note, tag: `reminder-${r.id}` });
        } catch { /* in-app message only */ }
        pushToast({ id: `reminder-${r.id}`, kind: 'reminder', title, body: r.note });
      });
    };
    check();
    const id = setInterval(check, 15000);
    return () => clearInterval(id);
  }, [list]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}
